import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorId: string | null;
}

/**
 * Root error boundary for CORTEX.
 *
 * Catches React render-tree errors and replaces the affected sub-tree with
 * a non-leaking generic notice. Never displays the raw error or stack to
 * end users — see docs/SECURITY.md (IP protection).
 *
 * In PR #4 the error report will be POSTed to /api/telemetry/exception
 * with a redaction-aware payload (no PII, no secrets, no quote data).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const errorId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `err_${Date.now()}`;
    this.setState({ errorId });

    console.error(`[CORTEX:${errorId}]`, error.message, { stack: info.componentStack });
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true, errorId: null };
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-slate-100 p-8 font-sans"
      >
        <div className="max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
          <h1 className="text-lg font-black text-hr-indigo mb-2 tracking-tight">
            Something went wrong.
          </h1>
          <p className="text-xs text-slate-500 mb-6 leading-relaxed">
            CORTEX encountered an unexpected error in this view. The session is intact and your data
            has not been lost. Please reload to continue. If the problem persists, contact the
            platform team and quote the reference below.
          </p>
          {this.state.errorId && (
            <p className="text-micro font-mono text-slate-400 mb-6">Ref: {this.state.errorId}</p>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            className="bg-hr-pink hover:bg-hr-pink-hover text-white font-bold py-2.5 px-6 rounded-xl shadow text-xs uppercase tracking-widest"
          >
            Reload Session
          </button>
        </div>
      </div>
    );
  }
}
